import React, { useState } from 'react';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Badge from '@mui/material/Badge';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import useGlobalStyles from '../../styles';
import useStyles from '../../styles/location';
import { LocationAlertsPartial, PropLocationItem } from '../../services/api';
import CreateLocation from './CreateLocation';
import { setCurrentLocation } from '../../state/actions';
import Loading from '../../components/Loading';
import { useAppDispatch } from '../../state/store';

interface SubLocationsListProps {
  locationDetails: PropLocationItem;
  sortedChildLocations: PropLocationItem[] | undefined;
  updateLocationChildren: () => void;
  loadingLocations: boolean;
  locationAlertsPartial: LocationAlertsPartial[];
}

function SubLocationsList({
  locationDetails,
  sortedChildLocations,
  updateLocationChildren,
  loadingLocations,
  locationAlertsPartial,
}: SubLocationsListProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const dispatch = useAppDispatch();
  const [showCreateLocationForm, setShowCreateLocationForm] = useState<boolean>(false);

  const showChildLocLists = () => (
    <List>
      {sortedChildLocations &&
        Object.keys(sortedChildLocations).length > 0 &&
        Object.entries(sortedChildLocations).map(([, locationDetail]) => {
          const locationAlerts = locationAlertsPartial.find((item) =>
            item.id.startsWith(locationDetail.id)
          );
          const hasActiveAlerts = locationAlerts?.alerts.find(
            (alert) => alert.alertStatus?.isActive === true
          );
          const AlertIcon = hasActiveAlerts ? NotificationsActiveIcon : NotificationsIcon;

          return (
            <Button
              key={locationDetail.id}
              onClick={() => dispatch(setCurrentLocation(locationDetail.id))}
              className={classes.childLocationListItem}
              startIcon={
                <Badge
                  badgeContent={
                    locationAlerts && (
                      <AlertIcon
                        fontSize="small"
                        color={`${hasActiveAlerts ? 'error' : 'success'}`}
                      />
                    )
                  }
                >
                  <LocationOnIcon />
                </Badge>
              }
            >
              {locationDetail.name}
            </Button>
          );
        })}
    </List>
  );

  return (
    <div>
      <List>
        <ListItem>
          <Grid size={{ sm: 8, xs: 8 }} style={{ display: 'flex' }}>
            <LocationOnIcon className={classes.icon} style={{ alignSelf: 'center' }} />
            <Typography variant="h6" style={{ marginLeft: '10px' }}>
              Sub Locations
            </Typography>
          </Grid>

          <Tooltip title="Add Location">
            <Grid size={{ sm: 4, xs: 4 }} style={{ textAlign: 'right' }}>
              <IconButton
                onClick={() => setShowCreateLocationForm(!showCreateLocationForm)}
                size="large"
              >
                {showCreateLocationForm ? (
                  <CancelIcon className={globalClasses.closeBtnIcon} />
                ) : (
                  <AddLocationIcon className={globalClasses.icon} />
                )}
              </IconButton>
            </Grid>
          </Tooltip>
        </ListItem>
      </List>
      <div style={{ padding: '0 1rem' }}>
        {loadingLocations ? <Loading /> : !showCreateLocationForm && showChildLocLists()}

        {showCreateLocationForm && (
          <CreateLocation
            locationDetails={locationDetails}
            updateLocationChildren={updateLocationChildren}
            setShowCreateLocationForm={setShowCreateLocationForm}
          />
        )}
      </div>
    </div>
  );
}

export default SubLocationsList;
